// Users management section - handles CRUD operations for users (admin only)
import React, { useState, useMemo } from "react";
import { useToast } from "../providers/ToastProvider";
import { useLocalization } from "../providers/LocalizationProvider";
import { useRestAuth } from "../providers/RestAuthProvider";
import { useEditing } from "../lib/hooks/useEditing";
import { useFilter } from "../lib/hooks/useFilter";
import { useDataSource } from "../lib/hooks/useDataSource";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import EditorModal from "../components/ui/EditorModal";
import Confirm from "../components/ui/Confirm";
import { Input, Label } from "../components/ui/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import ResponsiveTable from "../components/ui/ResponsiveTable";
import { USER_ROLES } from "../lib/config/constants";
import { logger } from "../lib/utils/logger";
import { formatDateTime } from "../lib/utils/time";

export default function UsersSection({ dataSource, showTrash }){
  // Get hooks for notifications, authentication, and localization
  const { notify } = useToast();
  const { t, language } = useLocalization();
  const { user, refreshUser } = useRestAuth();
  
  // Use data source hook for user operations
  const {
    data: users,
    isLoading,
    error,
    create,
    update,
    softDelete,
    restore,
    hardDelete,
    refresh
  } = useDataSource(dataSource, 'users');

  const filter = useFilter(""); 
  const { editing, setEditing, openNew, openEdit, close } = useEditing({ name:'', firstName:'', email:'', password:'', role:'user', commonLanguage:'fr', culturalLanguage:'', });
  const [confirm, setConfirm] = useState({ open:false });
  const data = useMemo(() => 
    users.filter(u => 
      (showTrash ? u.deletedAt : !u.deletedAt) && 
      (`${u.firstName || ''} ${u.name || ''}`.toLowerCase().includes(filter.value.toLowerCase()) || 
       (u.email || '').toLowerCase().includes(filter.value.toLowerCase()))
    ), 
    [users, showTrash, filter.value]
  );

  const onSave = async (e) => { 
    e.preventDefault(); 
    
    // Check if current user is admin before allowing user creation
    if (!editing?.id && user?.role !== USER_ROLES.ADMIN) {
      notify({ type: 'error', message: 'Seuls les administrateurs peuvent créer des utilisateurs.' });
      return;
    }
    
    const f = new FormData(e.currentTarget);
    const item = {
      name: f.get('name') + "",
      firstName: f.get('firstName') + "",
      email: f.get('email') + "",
      password: f.get('password') + "",
      // Disabled role select (editing yourself) is not submitted, so leave role unchanged
      role: /** @type {UserRole} */ (f.get('role') ? f.get('role') + "" : undefined),
      commonLanguage: (f.get('commonLanguage') + "") || 'fr',
      culturalLanguage: (f.get('culturalLanguage') + "") || null,
    }; 
    
    try {
      if (editing?.id) {
        await update(editing.id, item);
        notify({ type: 'success', message: t('users.userUpdated') });
        if (editing.id === user?.id) {
          await refreshUser();
        }
      } else {
        await create(item);
        notify({ type: 'success', message: t('users.userCreated') });
      }
      setEditing(null);
      await refresh(true);
    } catch (error) {
      // Error is already handled by the hook
      logger.error('Save error', error);
    }
  };

  const askDelete = (u) => { 
    // Note: We don't have access to responses here, so we'll simplify this for now
    setConfirm({ 
      open:true, 
      title:`Supprimer l'utilisateur « ${u.name} » ?`, 
      description: "", 
      onConfirm: async ()=>{ 
        await softDelete(u.id); 
        notify({ type:'success', message:'Utilisateur déplacé dans la corbeille.'}); 
        setConfirm({ open:false }); 
      }, 
      onCancel: ()=>setConfirm({ open:false }) 
    }); 
  };

  // Render loading state
  if (isLoading && users.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('users.title')}</CardTitle>
            <div className="flex items-center gap-2">
              {filter.input}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
              <span className="text-slate-500">{t('common.loading')}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (error && users.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('users.title')}</CardTitle>
            <div className="flex items-center gap-2">
              {filter.input}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="text-red-600 text-center">
              <p className="font-medium">{t('common.loadingError')}</p>
              <p className="text-sm">{error}</p>
            </div>
            <Button
              onClick={() => refresh(true)}
            >
              {t('common.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="space-y-4">
          <CardTitle>{t('users.title')}</CardTitle>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
            {!showTrash && user?.role === USER_ROLES.ADMIN && (
              <Button onClick={openNew} className="w-full sm:w-auto">
                {t('common.new')}
              </Button>
            )}
            <div className="w-full sm:w-auto">
              {filter.input}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveTable
          data={data}
          columns={[
            { header: t('common.name') },
            { header: t('users.firstName') },
            { header: t('users.email') },
            { header: t('users.role') },
            { header: t('users.commonLanguage') },
            { header: t('users.culturalLanguage') },
            { header: t('common.actions'), className: "w-56 text-right" }
          ]}
          renderRow={(u) => (
            <tr key={u.id} className="border-t">
              <td className="py-2">{u.name}</td>
              <td className="py-2">{u.firstName||t('common.notAssigned')}</td>
              <td className="py-2">{u.email||t('common.notAssigned')}</td>
              <td className="py-2">{u.role}</td>
              <td className="py-2">{u.commonLanguage||t('common.notAssigned')}</td>
              <td className="py-2">{u.culturalLanguage||u.cultureLanguage||t('common.notAssigned')}</td>
              <td className="py-2 text-right space-x-2">
                {!showTrash ? (
                  <>
                    <Button 
                      onClick={() => openEdit(u)} 
                      variant="outline"
                      size="sm"
                    >
                      {t('common.edit')}
                    </Button>
                    <Button 
                      onClick={() => askDelete(u)} 
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                    >
                      {t('common.delete')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      onClick={async()=>{ await restore(u.id); notify({ type:'success', message:t('users.userRestored')}); }} 
                      variant="outline"
                      size="sm"
                    >
                      {t('common.restore')}
                    </Button>
                    <Button 
                      onClick={async()=>{ await hardDelete(u.id); notify({ type:'success', message:t('users.userDeletedPermanent')}); }} 
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                    >
                      {t('common.deletePermanent')}
                    </Button>
                  </>
                )}
              </td>
            </tr>
          )}
          renderMobileCard={(u) => (
            <div className="space-y-3 w-full max-w-full overflow-hidden">
              <div className="w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium text-muted-foreground mb-1">{t('common.name')}</div>
                <div className="text-sm break-words overflow-wrap-anywhere">{u.name}</div>
              </div>
              <div className="w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium text-muted-foreground mb-1">{t('users.firstName')}</div>
                <div className="text-sm break-words overflow-wrap-anywhere">{u.firstName||t('common.notAssigned')}</div>
              </div>
              <div className="w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium text-muted-foreground mb-1">{t('users.email')}</div>
                <div className="text-sm break-words overflow-wrap-anywhere">{u.email||t('common.notAssigned')}</div>
              </div>
              <div className="flex justify-between items-center w-full max-w-full">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-muted-foreground mb-1">{t('users.role')}</div>
                  <div className="text-sm break-words">{u.role}</div>
                </div>
                <div className="min-w-0 flex-1 ml-2">
                  <div className="text-sm font-medium text-muted-foreground mb-1">{t('users.commonLanguage')}</div>
                  <div className="text-sm break-words">{u.commonLanguage||t('common.notAssigned')}</div>
                </div>
              </div>
              <div className="w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium text-muted-foreground mb-1">{t('users.culturalLanguage')}</div>
                <div className="text-sm break-words overflow-wrap-anywhere">{u.culturalLanguage||u.cultureLanguage||t('common.notAssigned')}</div>
              </div>
              <div className="flex gap-2 pt-2 w-full max-w-full">
                {!showTrash ? (
                  <>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={()=>openEdit(u)} 
                      className="flex-1 min-w-0"
                    >
                      {t('common.edit')}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={()=>askDelete(u)} 
                      className="flex-1 min-w-0 text-destructive hover:text-destructive"
                    >
                      {t('common.delete')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={async()=>{ await restore(u.id); notify({ type:'success', message:t('users.userRestored')}); }} 
                      className="flex-1 min-w-0"
                    >
                      {t('common.restore')}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={async()=>{ await hardDelete(u.id); notify({ type:'success', message:t('users.userDeletedPermanent')}); }} 
                      className="flex-1 min-w-0 text-destructive hover:text-destructive"
                    >
                      {t('common.deletePermanent')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
          emptyMessage={t('common.noItems')}
        />
      </CardContent>

      <EditorModal open={!!editing} title={editing?.id ? t('users.editUser') : t('users.newUser')} onClose={close} onSubmit={onSave}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('common.name')} *</Label>
            <Input 
              id="name" 
              name="name" 
              defaultValue={editing?.name||''} 
              required 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="firstName">{t('users.firstName')}</Label>
            <Input 
              id="firstName" 
              name="firstName" 
              defaultValue={editing?.firstName||''} 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t('users.email')}</Label>
            <Input 
              id="email" 
              name="email" 
              type="email" 
              defaultValue={editing?.email||''} 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.password')}{!editing?.id && ' *'}</Label>
            <Input 
              id="password" 
              name="password" 
              type="password" 
              autoComplete="new-password"
              required={!editing?.id} 
            />
            {editing?.id && (
              <p className="text-xs text-muted-foreground">{t('profile.passwordHint')}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">{t('users.role')}</Label>
            <Select name="role" defaultValue={editing?.role||'user'} disabled={!!editing?.id && editing.id === user?.id}>
              <SelectTrigger>
                <SelectValue placeholder={t('users.selectRole')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={USER_ROLES.ADMIN}>{t('roles.admin')}</SelectItem>
                <SelectItem value="user">{t('roles.user')}</SelectItem>
                <SelectItem value="representative">{t('roles.representative')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="commonLanguage">{t('users.commonLanguage')}</Label>
            <Select name="commonLanguage" defaultValue={editing?.commonLanguage||'fr'}>
              <SelectTrigger>
                <SelectValue placeholder={t('users.selectLanguage')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="pt">Português</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="culturalLanguage">{t('users.culturalLanguage')}</Label>
            <Input 
              id="culturalLanguage" 
              name="culturalLanguage" 
              defaultValue={editing?.culturalLanguage||editing?.cultureLanguage||''} 
            />
          </div>
          {editing?.id && (
            <>
              <div className="space-y-2">
                <Label htmlFor="createdAt">{t('common.createdAt')}</Label>
                <Input
                  id="createdAt"
                  value={formatDateTime(editing?.createdAt, language)}
                  readOnly
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="updatedAt">{t('common.updatedAt')}</Label>
                <Input
                  id="updatedAt"
                  value={formatDateTime(editing?.updatedAt, language)}
                  readOnly
                  disabled
                  className="bg-muted"
                />
              </div>
            </>
          )}
        </div>
      </EditorModal>
      <Confirm {...confirm} />
    </Card>
  );
}